import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  ComplianceTestOptionDto,
  RedesignReasonOptionDto,
  SlurryRedesignReasonsResponseDto,
  SlurryService
} from '../../services/slurry.service';

export interface AddNewSlurryResult {
  isRedesign: boolean;
  reasonTypeId: number | null;
  comments: string;
  complianceTestIds: number[];
}

@Component({
  selector: 'app-add-new-slurry-modal',
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './add-new-slurry-modal.component.html',
  styleUrls: ['./add-new-slurry-modal.component.css']
})
export class AddNewSlurryModalComponent implements OnInit {
  projectTypeId: number;

  isLoading = false;
  errorMessage = '';

  isRedesign = true;
  reasons: RedesignReasonOptionDto[] = [];
  selectedReasonTypeId: number | null = null;
  comments = '';
  selectedComplianceTestIds: number[] = [];

  constructor(
    private slurryService: SlurryService,
    private dialogRef: MatDialogRef<AddNewSlurryModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: { projectTypeId: number }
  ) {
    this.projectTypeId = data?.projectTypeId ?? 1;
  }

  ngOnInit(): void {
    this.loadRedesignReasons();
  }

  get selectedReason(): RedesignReasonOptionDto | undefined {
    return this.reasons.find(r => r.reasonTypeId === this.selectedReasonTypeId);
  }

  get availableComplianceTests(): ComplianceTestOptionDto[] {
    return this.selectedReason?.availableComplianceTests ?? [];
  }

  get requiresComplianceTests(): boolean {
    return this.selectedReason?.requiresComplianceTests ?? false;
  }

  get canSubmit(): boolean {
    if (!this.isRedesign) {
      return true;
    }

    if (!this.selectedReasonTypeId) {
      return false;
    }

    if (this.requiresComplianceTests && this.selectedComplianceTestIds.length === 0) {
      return false;
    }

    return true;
  }

  loadRedesignReasons(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.slurryService.getRedesignReasons(this.projectTypeId).subscribe({
      next: (response: SlurryRedesignReasonsResponseDto) => {
        this.reasons = response.reasons ?? [];
        this.selectedReasonTypeId = this.reasons.length > 0 ? 0 : null;
        this.selectedComplianceTestIds = [];
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load redesign reasons. Please try again.';
        this.reasons = [];
        this.selectedReasonTypeId = null;
        this.selectedComplianceTestIds = [];
        this.isLoading = false;
      }
    });
  }

  onReasonChanged(): void {
    this.selectedComplianceTestIds = [];
  }

  onComplianceTestToggle(testTypeId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedComplianceTestIds.includes(testTypeId)) {
        this.selectedComplianceTestIds.push(testTypeId);
      }
      return;
    }

    this.selectedComplianceTestIds = this.selectedComplianceTestIds.filter(id => id !== testTypeId);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.canSubmit) {
      return;
    }

    this.dialogRef.close({
      isRedesign: this.isRedesign,
      reasonTypeId: this.isRedesign ? this.selectedReasonTypeId : null,
      comments: this.comments?.trim() ?? '',
      complianceTestIds: this.isRedesign ? [...this.selectedComplianceTestIds] : []
    });
  }
}
